import {
  Avatar,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuList,
  Portal,
  useToast,
} from '@chakra-ui/react';
import { IconArrowBoxLeft } from 'central-icons/IconArrowBoxLeft';
import { IconEyeOpen } from 'central-icons/IconEyeOpen';
import { IconPeople } from 'central-icons/IconPeople';
import { IconSettingsGear2 } from 'central-icons/IconSettingsGear2';
import { IconShieldKeyhole } from 'central-icons/IconShieldKeyhole';
import { IconTrashCan } from 'central-icons/IconTrashCan';
import { useNavigate } from 'react-router';

import {
  clearStorage,
  DASH_META_ROOT,
  fullLogout,
  isRoleRevelioAdmin,
  useGetLoggedInUser,
} from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { Role } from '@revelio/data-access';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { MenuItemWithIcon } from './menu-item-with-icon';

interface AccountMenuProps {
  version?: string;
  onOpenImpersonationModal?: () => void;
}

export function AccountMenu({
  version,
  onOpenImpersonationModal,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const { loggedInUser, realLoggedInUser } = useGetLoggedInUser();
  const isImpersonationEnabled = useUnleashFlag(FeatureFlag.Impersonation);
  const adminRole = realLoggedInUser?.role ?? loggedInUser.role;
  const isRevelioAdmin = isRoleRevelioAdmin(adminRole);
  const userId = loggedInUser.id;
  const username = loggedInUser.name;

  return (
    <Menu placement="right-start" autoSelect={false}>
      <MenuButton _hover={{ opacity: 0.7 }} data-testid="user-controls">
        <Avatar size="sm" bg="lightBlue.500" />
      </MenuButton>
      <Portal>
        <MenuList
          boxShadow="xl"
          // workaround to fix focus shadow bugadow
          // ensures popover has box shadow when open
          sx={{
            ':focus:not(:focus-visible)': {
              shadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important',
            },
          }}
        >
          <MenuGroup title={username as string}>
            <MenuItemWithIcon
              onClick={() => navigate(`/manage/user/${userId}/edit`)}
              icon={IconSettingsGear2}
            >
              Account
            </MenuItemWithIcon>
            {isRevelioAdmin && (
              <MenuItemWithIcon
                onClick={() => navigate('/admin')}
                icon={IconShieldKeyhole}
              >
                Admin
              </MenuItemWithIcon>
            )}
            {isRevelioAdmin && isImpersonationEnabled && (
              <MenuItemWithIcon
                onClick={onOpenImpersonationModal}
                icon={IconEyeOpen}
                data-testid="impersonation-settings-menu-item"
              >
                Impersonation Settings
              </MenuItemWithIcon>
            )}

            <MenuDivider />

            {isRevelioAdmin && (
              <MenuItemWithIcon
                onClick={async () => {
                  const clearSiteData = fetch(
                    `${DASH_META_ROOT}/api/auth/clear-site-data?types="cache", "storage"`,
                    {
                      credentials: 'include',
                      headers: { 'request-id': crypto.randomUUID() },
                    }
                  );

                  const manualClear = clearStorage();

                  const clearAll = Promise.all([clearSiteData, manualClear]);

                  toast.promise(clearAll, {
                    loading: {
                      title: 'Clearing Cache',
                      description: 'Please wait a moment...',
                      position: 'top-right',
                    },
                    success: {
                      title: 'Cache Cleared',
                      position: 'top-right',
                      duration: 1000,
                      onCloseComplete: () => window.location.reload(),
                    },
                    error: (error) => ({
                      title: 'Error Clearing Cache',
                      description: 'Please try again.',
                      position: 'top-right',
                      onCloseComplete: () => console.error(error),
                    }),
                  });
                }}
                icon={IconTrashCan}
              >
                Clear Cache
              </MenuItemWithIcon>
            )}
            {loggedInUser.role === Role.ClientAdmin && (
              <MenuItemWithIcon
                onClick={() => navigate('/manage')}
                icon={IconPeople}
              >
                User Management
              </MenuItemWithIcon>
            )}
            <MenuItemWithIcon
              onClick={() => {
                fullLogout(true);
              }}
              icon={IconArrowBoxLeft}
            >
              Logout
            </MenuItemWithIcon>
            {version && (
              <>
                <MenuDivider />
                <MenuGroup title={version} opacity={0.4} mb={1} />
              </>
            )}
          </MenuGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
}

export default AccountMenu;
